import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getOrderById,
  updateOrder,
  createOrderEstimate,
  updateOrderEstimate,
  createOrderTimelineEvent,
  createLedgerEntry,
  dispatchOrder,
  confirmOrderReceipt,
} from '../../store/actions';
import Badge from '../ui/Badge';
import { ORDER_RECEIPT_STATUS, ORDER_STATUS, formatDate, formatINR, label, today } from '../../constants/garmentCodes';

export const ORDER = {
  DRAFT: 1,
  SUBMITTED: 2,
  ESTIMATE_PENDING: 3,
  ESTIMATE_APPROVED: 4,
  CONFIRMED: 5,
  IN_PRODUCTION: 6,
  READY: 7,
  DISPATCHED: 8,
  DELIVERED: 9,
};

const COST_FIELDS = [
  ['fabricCost', 'Fabric'],
  ['designCost', 'Design'],
  ['samplingCost', 'Sampling'],
  ['productionCost', 'Production'],
  ['packagingCost', 'Packaging'],
  ['dispatchCost', 'Dispatch'],
];
const TABS = ['Overview', 'Colour Matrix', 'Cost Estimate', 'Dispatch', 'Receipt', 'Timeline'];
const inputCls = 'w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400';

const estimateTotal = (est) => COST_FIELDS.reduce((sum, [k]) => sum + (Number(est?.[k]) || 0), 0);

function EstimateForm({ order, onSubmit }) {
  const [form, setForm] = useState(() =>
    Object.fromEntries([['unitCost', order.estimate?.unitCost ?? ''], ...COST_FIELDS.map(([k]) => [k, order.estimate?.[k] ?? ''])]),
  );
  const total = estimateTotal(form);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === '' ? null : Number(v)])), total);
      }}
      className="space-y-3"
    >
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Unit Cost</label>
          <input type="number" min="0" value={form.unitCost} onChange={(e) => setForm({ ...form, unitCost: e.target.value })} className={`${inputCls} font-mono`} />
        </div>
        {COST_FIELDS.map(([k, text]) => (
          <div key={k}>
            <label className="block text-[11px] font-medium text-gray-600 mb-1">{text} Cost</label>
            <input type="number" min="0" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={`${inputCls} font-mono`} />
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3">
        <span className="text-[12px] text-gray-500">Total Estimate</span>
        <span className="text-[16px] font-bold font-mono text-gray-900">{formatINR(total)}</span>
      </div>
      <button type="submit" className="w-full px-4 py-2.5 text-[12px] font-medium text-white bg-[#1b3a6b] rounded-lg hover:bg-[#152d55]">Submit Estimate to Brand</button>
    </form>
  );
}

function DispatchForm({ onSubmit }) {
  const [form, setForm] = useState({ courier: '', trackingNumber: '', dispatchDate: today(), packageCount: '', expectedDelivery: '', notes: '' });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({ ...form, packageCount: form.packageCount ? Number(form.packageCount) : null, expectedDelivery: form.expectedDelivery || null, notes: form.notes || null });
      }}
      className="space-y-3"
    >
      <div className="grid grid-cols-2 gap-3">
        <div><label className="block text-[11px] font-medium text-gray-600 mb-1">Courier *</label><input required value={form.courier} onChange={set('courier')} className={inputCls} /></div>
        <div><label className="block text-[11px] font-medium text-gray-600 mb-1">Tracking Number *</label><input required value={form.trackingNumber} onChange={set('trackingNumber')} className={`${inputCls} font-mono`} /></div>
        <div><label className="block text-[11px] font-medium text-gray-600 mb-1">Dispatch Date *</label><input required type="date" value={form.dispatchDate} onChange={set('dispatchDate')} className={inputCls} /></div>
        <div><label className="block text-[11px] font-medium text-gray-600 mb-1">Package Count</label><input type="number" min="1" value={form.packageCount} onChange={set('packageCount')} className={`${inputCls} font-mono`} /></div>
        <div className="col-span-2"><label className="block text-[11px] font-medium text-gray-600 mb-1">Expected Delivery</label><input type="date" value={form.expectedDelivery} onChange={set('expectedDelivery')} className={inputCls} /></div>
      </div>
      <textarea rows={2} value={form.notes} onChange={set('notes')} placeholder="Dispatch notes" className={inputCls} />
      <button type="submit" className="w-full px-4 py-2.5 text-[12px] font-medium text-white bg-[#1b3a6b] rounded-lg hover:bg-[#152d55]">Record Dispatch</button>
    </form>
  );
}

function ReceiptForm({ order, onSubmit }) {
  const [form, setForm] = useState({ receivedQty: order.totalQty ?? '', receiptDate: today(), condition: 'Good condition', comments: '', receiptStatus: 2 });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({ ...form, receivedQty: form.receivedQty === '' ? null : Number(form.receivedQty), receiptStatus: Number(form.receiptStatus) });
      }}
      className="space-y-3"
    >
      <div className="grid grid-cols-2 gap-3">
        <div><label className="block text-[11px] font-medium text-gray-600 mb-1">Received Qty</label><input type="number" min="0" value={form.receivedQty} onChange={set('receivedQty')} className={`${inputCls} font-mono`} /></div>
        <div><label className="block text-[11px] font-medium text-gray-600 mb-1">Receipt Date</label><input type="date" value={form.receiptDate} onChange={set('receiptDate')} className={inputCls} /></div>
        <div>
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Condition</label>
          <select value={form.condition} onChange={set('condition')} className={inputCls}>
            <option>Good condition</option>
            <option>Partially damaged</option>
            <option>Damaged</option>
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Outcome</label>
          <select value={form.receiptStatus} onChange={set('receiptStatus')} className={inputCls}>
            <option value={2}>Confirmed</option>
            <option value={3}>Disputed</option>
          </select>
        </div>
      </div>
      <textarea rows={2} value={form.comments} onChange={set('comments')} placeholder="Comments" className={inputCls} />
      <button type="submit" className="w-full px-4 py-2.5 text-[12px] font-medium text-white bg-green-600 rounded-lg hover:bg-green-700">Confirm Receipt</button>
    </form>
  );
}

export default function OrderDetailDrawer({ orderId, role, onClose }) {
  const dispatch = useDispatch();
  const order = useSelector((state) => state.order.order);
  const userId = useSelector((state) => state.auth?.loginUser?.userId ?? null);
  const [tab, setTab] = useState('Overview');
  const isBrand = role === 'brand';

  useEffect(() => {
    if (orderId) {
      setTab('Overview');
      dispatch(getOrderById({ id: orderId }));
    }
  }, [dispatch, orderId]);

  if (!orderId) return null;
  const loaded = order && String(order.id) === String(orderId);
  const reload = () => dispatch(getOrderById({ id: orderId }));

  const advance = (status, extra = {}) => {
    dispatch(
      updateOrder({
        payload: { id: order.id, status, ...extra },
        callback: {
          success: () => {
            dispatch(createOrderTimelineEvent({ payload: { orderId: order.id, status, eventDate: today(), userId } }));
            reload();
          },
        },
      }),
    );
  };

  const submitEstimate = (costs, total) => {
    const body = { ...costs, orderId: order.id };
    const onSaved = { success: () => advance(ORDER.ESTIMATE_PENDING, { totalEstimate: total }) };
    if (order.estimate?.id) dispatch(updateOrderEstimate({ payload: { ...body, id: order.estimate.id }, callback: onSaved }));
    else dispatch(createOrderEstimate({ payload: body, callback: onSaved }));
  };

  const approveEstimate = () => {
    advance(ORDER.ESTIMATE_APPROVED, { walletHold: order.totalEstimate });
    dispatch(
      createLedgerEntry({
        payload: {
          engagementId: order.engagementId,
          type: 4,
          category: 6,
          date: today(),
          postingDate: today(),
          description: `Order hold — ${order.orderNumber || `order #${order.id}`}`,
          reference: order.orderNumber,
          held: order.totalEstimate,
          status: 2,
          approvalStatus: 1,
          postedByUserId: userId,
          linkedItemType: 'Order',
          linkedItemId: order.id,
        },
      }),
    );
  };

  const recordDispatch = (payload) =>
    dispatch(dispatchOrder({ payload: { id: order.id, status: ORDER.DISPATCHED, ...payload }, callback: { success: reload } }));

  const confirmReceipt = (payload) =>
    dispatch(
      confirmOrderReceipt({
        payload: { id: order.id, ...payload },
        callback: { success: () => payload.receiptStatus === 2 && advance(ORDER.DELIVERED) },
      }),
    );

  const status = loaded ? label(ORDER_STATUS, order.status) : '';
  const matrix = loaded ? order.colourMatrix : null;

  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-[560px] max-w-full bg-white shadow-2xl overflow-y-auto flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-[14px] font-semibold text-gray-900">{loaded ? order.orderNumber || `Order #${order.id}` : 'Loading…'}</h2>
            {loaded && <p className="text-[11px] text-gray-400">{order.brandName} · {order.product}</p>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>

        {loaded && (
          <>
            <div className="flex gap-0.5 px-6 border-b border-gray-100 overflow-x-auto">
              {TABS.map((t) => (
                <button key={t} onClick={() => setTab(t)} className={`px-3 py-2.5 text-[11px] font-medium whitespace-nowrap border-b-2 -mb-px ${tab === t ? 'border-[#1b3a6b] text-[#1b3a6b]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>{t}</button>
              ))}
            </div>

            <div className="flex-1 p-6 space-y-4">
              {tab === 'Overview' && (
                <>
                  <div className="grid grid-cols-2 gap-3 text-[12px]">
                    <div><p className="text-gray-400">Status</p><Badge status={status} /></div>
                    <div><p className="text-gray-400">Target Delivery</p><p className="font-medium">{formatDate(order.targetDelivery)}</p></div>
                    <div><p className="text-gray-400">Design</p><p className="font-medium">{order.designName || '—'}</p></div>
                    <div><p className="text-gray-400">Fabric</p><p className="font-medium">{order.fabricName || '—'}</p></div>
                    <div><p className="text-gray-400">Total Qty</p><p className="font-mono font-semibold">{order.totalQty ?? '—'} pcs</p></div>
                    <div><p className="text-gray-400">Coordinator</p><p className="font-medium">{order.coordinator || '—'}</p></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Total Estimate</p>
                      <p className="text-[18px] font-bold font-mono text-gray-900">{order.totalEstimate ? formatINR(order.totalEstimate) : '—'}</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-3">
                      <p className="text-[10px] text-amber-600 uppercase tracking-wide mb-1">Wallet Hold</p>
                      <p className="text-[18px] font-bold font-mono text-amber-700">{order.walletHold ? formatINR(order.walletHold) : '—'}</p>
                    </div>
                  </div>

                  {!isBrand && (
                    <div className="flex gap-2 flex-wrap">
                      {(order.status === ORDER.SUBMITTED || order.status === ORDER.DRAFT) && (
                        <button onClick={() => setTab('Cost Estimate')} className="flex-1 px-4 py-2.5 text-[12px] font-medium text-white bg-[#1b3a6b] rounded-lg">Prepare Estimate</button>
                      )}
                      {order.status === ORDER.ESTIMATE_APPROVED && (
                        <button onClick={() => advance(ORDER.CONFIRMED)} className="flex-1 px-4 py-2.5 text-[12px] font-medium text-white bg-[#1b3a6b] rounded-lg">Confirm Order</button>
                      )}
                      {order.status === ORDER.CONFIRMED && (
                        <button onClick={() => advance(ORDER.IN_PRODUCTION)} className="flex-1 px-4 py-2.5 text-[12px] font-medium text-white bg-[#1b3a6b] rounded-lg">Start Production</button>
                      )}
                      {order.status === ORDER.IN_PRODUCTION && (
                        <button onClick={() => advance(ORDER.READY)} className="flex-1 px-4 py-2.5 text-[12px] font-medium text-[#1b3a6b] border border-[#1b3a6b]/30 rounded-lg">Mark Ready</button>
                      )}
                      {(order.status === ORDER.IN_PRODUCTION || order.status === ORDER.READY) && (
                        <button onClick={() => setTab('Dispatch')} className="flex-1 px-4 py-2.5 text-[12px] font-medium text-white bg-[#1b3a6b] rounded-lg">Record Dispatch</button>
                      )}
                    </div>
                  )}
                  {isBrand && order.status === ORDER.ESTIMATE_PENDING && (
                    <button onClick={() => setTab('Cost Estimate')} className="w-full px-4 py-2.5 text-[12px] font-medium text-white bg-[#1E2A3B] rounded-lg">Review Estimate</button>
                  )}
                  {isBrand && order.status === ORDER.DISPATCHED && (
                    <button onClick={() => setTab('Receipt')} className="w-full px-4 py-2.5 text-[12px] font-medium text-white bg-green-600 rounded-lg">Confirm Receipt</button>
                  )}
                </>
              )}

              {tab === 'Colour Matrix' && (
                matrix?.colours?.length ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-[12px]">
                      <thead>
                        <tr className="border-b border-gray-100">
                          <th className="text-left py-2 pr-3 text-gray-400 font-medium">Colour</th>
                          {matrix.sizes.map((s) => <th key={s} className="text-right py-2 px-2 text-gray-400 font-medium">{s}</th>)}
                          <th className="text-right py-2 pl-2 text-gray-400 font-medium">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {matrix.colours.map((c) => {
                          const row = matrix.quantities?.[c] || {};
                          const rowTotal = matrix.sizes.reduce((sum, s) => sum + (Number(row[s]) || 0), 0);
                          return (
                            <tr key={c} className="border-b border-gray-50">
                              <td className="py-2 pr-3 font-medium text-gray-800">{c}</td>
                              {matrix.sizes.map((s) => <td key={s} className="text-right py-2 px-2 font-mono">{row[s] ?? '—'}</td>)}
                              <td className="text-right py-2 pl-2 font-mono font-semibold">{rowTotal}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-[12px] text-gray-400 text-center py-8">No colour / size breakdown recorded</p>
                )
              )}

              {tab === 'Cost Estimate' && (
                <>
                  {order.estimate && (
                    <div className="space-y-2">
                      {[['unitCost', 'Unit Cost'], ...COST_FIELDS].map(([k, text]) => (
                        <div key={k} className="flex justify-between text-[12px] border-b border-gray-50 py-1.5">
                          <span className="text-gray-500">{k === 'unitCost' ? text : `${text} Cost`}</span>
                          <span className="font-mono text-gray-800">{formatINR(order.estimate[k])}</span>
                        </div>
                      ))}
                      <div className="flex justify-between text-[13px] font-semibold pt-1">
                        <span>Total</span>
                        <span className="font-mono">{formatINR(estimateTotal(order.estimate))}</span>
                      </div>
                    </div>
                  )}
                  {!isBrand && [ORDER.DRAFT, ORDER.SUBMITTED, ORDER.ESTIMATE_PENDING].includes(order.status) && <EstimateForm order={order} onSubmit={submitEstimate} />}
                  {isBrand && order.status === ORDER.ESTIMATE_PENDING && order.estimate && (
                    <div className="flex gap-2">
                      <button onClick={() => advance(ORDER.SUBMITTED)} className="flex-1 px-4 py-2.5 text-[12px] font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50">Request Revision</button>
                      <button onClick={approveEstimate} className="flex-1 px-4 py-2.5 text-[12px] font-medium text-white bg-green-600 rounded-lg hover:bg-green-700">Approve & Hold Funds</button>
                    </div>
                  )}
                  {!order.estimate && isBrand && <p className="text-[12px] text-gray-400 text-center py-8">The manufacturer has not submitted an estimate yet</p>}
                </>
              )}

              {tab === 'Dispatch' && (
                <>
                  {order.dispatch ? (
                    <div className="grid grid-cols-2 gap-3 text-[12px]">
                      <div><p className="text-gray-400">Courier</p><p className="font-medium">{order.dispatch.courier || '—'}</p></div>
                      <div><p className="text-gray-400">Tracking</p><p className="font-mono">{order.dispatch.trackingNumber || '—'}</p></div>
                      <div><p className="text-gray-400">Dispatched</p><p className="font-medium">{formatDate(order.dispatch.dispatchDate)}</p></div>
                      <div><p className="text-gray-400">Packages</p><p className="font-medium">{order.dispatch.packageCount ?? '—'}</p></div>
                      <div><p className="text-gray-400">Expected Delivery</p><p className="font-medium">{formatDate(order.dispatch.expectedDelivery)}</p></div>
                      {order.dispatch.notes && <div className="col-span-2"><p className="text-gray-400">Notes</p><p>{order.dispatch.notes}</p></div>}
                    </div>
                  ) : (
                    <p className="text-[12px] text-gray-400 text-center py-4">Not dispatched yet</p>
                  )}
                  {!isBrand && (order.status === ORDER.IN_PRODUCTION || order.status === ORDER.READY) && <DispatchForm onSubmit={recordDispatch} />}
                </>
              )}

              {tab === 'Receipt' && (
                <>
                  {order.receiptStatus ? (
                    <div className="grid grid-cols-2 gap-3 text-[12px]">
                      <div><p className="text-gray-400">Receipt Status</p><Badge status={label(ORDER_RECEIPT_STATUS, order.receiptStatus)} /></div>
                      <div><p className="text-gray-400">Received Qty</p><p className="font-mono">{order.receivedQty ?? '—'}</p></div>
                      <div><p className="text-gray-400">Received On</p><p className="font-medium">{formatDate(order.receivedDate)}</p></div>
                      <div><p className="text-gray-400">Condition</p><p className="font-medium">{order.receivedCondition || '—'}</p></div>
                      {order.receivedComments && <div className="col-span-2"><p className="text-gray-400">Comments</p><p>{order.receivedComments}</p></div>}
                    </div>
                  ) : (
                    <p className="text-[12px] text-gray-400 text-center py-4">Receipt not confirmed yet</p>
                  )}
                  {isBrand && order.status === ORDER.DISPATCHED && !order.receiptStatus && <ReceiptForm order={order} onSubmit={confirmReceipt} />}
                </>
              )}

              {tab === 'Timeline' && (
                (order.timeline || []).length ? (
                  <ol className="relative border-l border-gray-200 ml-2 space-y-4">
                    {order.timeline.map((ev) => (
                      <li key={ev.id} className="ml-4">
                        <span className="absolute -left-1.5 w-3 h-3 bg-[#1b3a6b] rounded-full" />
                        <p className="text-[12px] font-medium text-gray-800">{label(ORDER_STATUS, ev.status)}</p>
                        <p className="text-[11px] text-gray-400">{formatDate(ev.eventDate)}</p>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="text-[12px] text-gray-400 text-center py-8">No timeline events yet</p>
                )
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
